/**
 * 粘贴文本智能拆分（姓名、手机、单位、地址等），规则解析。
 */

const PHONE_RE = /1[3-9]\d{9}/
const COMPANY_SUFFIX =
  /([\u4e00-\u9fa5A-Za-z0-9（）()·-]{2,80}(?:有限责任公司|有限公司|股份公司|集团有限公司|协会|研究院|事务所|集团|公司|中心|单位))/
const NAME_WITH_TITLE =
  /^([\u4e00-\u9fa5]{1,4}(?:小姐|先生|女士|顾问|助理|秘书|专员|工程师|经理|主任|总监|总|老师|主管|董事|总裁|厂长|科长|处长|董事长))/
const NAME_PLAIN = /^([\u4e00-\u9fa5]{2,4})/
const ADDRESS_HINT =
  /(?:省|市|区|县|镇|乡|村|路|街|道|巷|弄|号|栋|室|层|楼|大厦|广场|工业园|高新区)/

function pickPhone(text) {
  const m = String(text || '').match(PHONE_RE)
  return m ? m[0] : ''
}

function removePhone(text, phone) {
  let s = String(text || '')
  if (phone) s = s.replace(phone, ' ')
  return s.replace(/\s+/g, ' ').trim()
}

function pickNameFromLabeled(text) {
  const m = String(text || '').match(
    /(?:姓名|联系人|收件人|拜访对象|客户|联系人姓名)\s*[:：]\s*([^\s,，;；\n]{2,12})/,
  )
  return m ? m[1].trim() : ''
}

function pickNameFromRest(rest) {
  const s = String(rest || '').trim()
  if (!s) return ''
  const labeled = pickNameFromLabeled(s)
  if (labeled) return labeled
  const titled = s.match(NAME_WITH_TITLE)
  if (titled) return titled[1].trim()
  const plain = s.match(NAME_PLAIN)
  if (plain && !COMPANY_SUFFIX.test(plain[1])) return plain[1].trim()
  const parts = s.split(/[\n,，;；\s]+/).map((x) => x.trim()).filter(Boolean)
  for (let i = 0; i < parts.length; i += 1) {
    const p = parts[i]
    if (p.length >= 2 && p.length <= 12 && !PHONE_RE.test(p) && !COMPANY_SUFFIX.test(p)) {
      return p
    }
  }
  return ''
}

function pickCompanyFromLabeled(text) {
  const m = String(text || '').match(
    /(?:单位|公司|企业|客户单位|单位名称)\s*[:：]\s*([^\n,，;；]{2,80})/,
  )
  return m ? m[1].trim() : ''
}

function pickCompanyFromRest(rest, name) {
  let s = String(rest || '').trim()
  if (!s) return ''
  const labeled = pickCompanyFromLabeled(s)
  if (labeled) return labeled
  if (name) s = s.replace(name, '').trim()
  const m = s.match(COMPANY_SUFFIX)
  if (m) return m[1].trim()
  return ''
}

function looksLikeAddress(text) {
  const s = String(text || '').trim()
  if (!s) return false
  if (/(?:有限责任公司|有限公司|股份公司|集团有限公司|协会|研究院|事务所|集团|公司|中心|单位)$/.test(s)) {
    return false
  }
  if (ADDRESS_HINT.test(s)) return true
  return /[\u4e00-\u9fa5]{2,6}省?[\u4e00-\u9fa5]{1,12}(?:市|州|盟)/.test(s)
}

function pickAddressFromLabeled(text) {
  const m = String(text || '').match(/(?:地址|详细地址|单位地址|走访地址)\s*[:：]\s*([^\n]+)/)
  return m ? m[1].trim() : ''
}

function pickAddressFromRest(rest, name, company) {
  let s = String(rest || '').trim()
  if (!s) return ''
  const labeled = pickAddressFromLabeled(s)
  if (labeled) return labeled
  if (name) s = s.replace(name, '').trim()
  if (company) s = s.replace(company, '').trim()
  s = s.replace(/^[，,;；\s]+/, '').trim()
  if (!s) return ''
  if (!looksLikeAddress(s)) return ''
  const m = s.match(
    /[\u4e00-\u9fa5]{2,6}省?[\u4e00-\u9fa5]{1,12}(?:市|州|盟)[\u4e00-\u9fa5\d#（）()号楼栋室层街道路巷弄号\s-]{2,120}/,
  )
  if (m) return m[0].trim()
  return looksLikeAddress(s) ? s : ''
}

export function parseContactPasteText(text) {
  const raw = String(text || '').trim()
  const phone = pickPhone(raw)
  let rest = removePhone(raw, phone)
  const nameLabeled = pickNameFromLabeled(raw)
  const companyLabeled = pickCompanyFromLabeled(raw)
  const addressLabeled = pickAddressFromLabeled(raw)
  if (nameLabeled || companyLabeled || addressLabeled) {
    return {
      phone: phone || pickPhone(raw),
      name: nameLabeled || pickNameFromRest(rest),
      company: companyLabeled || pickCompanyFromRest(rest, nameLabeled),
      address: addressLabeled || pickAddressFromRest(rest, nameLabeled, companyLabeled),
      raw,
    }
  }
  const name = pickNameFromRest(rest)
  if (name) rest = rest.replace(name, '').trim()
  const company = pickCompanyFromRest(rest, '')
  if (company) rest = rest.replace(company, '').trim()
  const address = pickAddressFromRest(rest, '', '')
  return { phone, name, company, address, raw }
}

const FIELD_KEY_RULES = {
  contactName: 'name',
  contactPhone: 'phone',
  companyName: 'company',
  visitAddress: 'address',
}

function fieldMatchers(f) {
  const label = String((f && f.label) || '').trim()
  const key = String((f && f.fieldKey) || '').trim()
  if (FIELD_KEY_RULES[key]) return [FIELD_KEY_RULES[key]]
  const kl = key.toLowerCase()
  if (/phone|mobile|tel/.test(kl) && !/name/.test(kl)) return ['phone']
  if (/company|unit|org/.test(kl) && !/name/.test(kl)) return ['company']
  if (/address|addr|location/.test(kl)) return ['address']
  if (/^contactname$|^name$/.test(kl)) return ['name']
  if (/手机|电话|联系方式/.test(label)) return ['phone']
  if (/单位|公司|企业|客户/.test(label)) return ['company']
  if (/地址|住址|定位/.test(label)) return ['address']
  if (/姓名|联系人|对接人|拜访|对象|收件人/.test(label)) return ['name']
  return []
}

function isSmartMappedField(f) {
  return fieldMatchers(f).length > 0
}

export function applySmartPasteToFormValues(schemaFields, values, parsed, opts) {
  const force = !!(opts && opts.force)
  const fields = Array.isArray(schemaFields) ? schemaFields : []
  const out = values && typeof values === 'object' ? { ...values } : {}
  const p = parsed && typeof parsed === 'object' ? parsed : {}
  fields.forEach((f) => {
    if (!f || !f.fieldKey || !isSmartMappedField(f)) return
    const k = f.fieldKey
    const cur = out[k]
    const empty = Array.isArray(cur) ? cur.length === 0 : !String(cur || '').trim()
    if (!empty && !force) return
    const rules = fieldMatchers(f)
    if (rules.includes('name')) out[k] = p.name || (force ? '' : out[k])
    else if (rules.includes('phone')) out[k] = p.phone || (force ? '' : out[k])
    else if (rules.includes('company')) out[k] = p.company || (force ? '' : out[k])
    else if (rules.includes('address')) out[k] = p.address || (force ? '' : out[k])
  })
  return out
}
